import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import MainLayout from '../components/MainLayout';
import axios from 'axios';
import { toast } from 'sonner';
import { useActiveCompany } from '../hooks/useActiveCompany';
import {
  FileText, Plus, Edit2, CheckCircle2, History, Star, Copy, Eye, X,
  Save, ShieldCheck, Sparkles, Layers, RefreshCw, FileCode, Check,
  Wrench, Code, Layout, ArrowUp, ArrowDown, Trash2, HelpCircle, FileCheck,
  Building2, User, DollarSign, PenTool, CheckSquare, EyeOff, Sliders,
  Type, AlignLeft, AlignCenter, AlignRight, AlignJustify, Bold, Italic, Underline,
  List, ListOrdered, Palette, FilePlus, ChevronRight, Wand2, Monitor, Download,
  CornerUpLeft, CornerUpRight, Image as ImageIcon, MoveUp, MoveDown, Strikethrough,
  Superscript, Subscript, Highlighting, Table, Scissors, FileBox, QrCode, Maximize2,
  Minimize2, ZoomIn, ZoomOut, RotateCw, Settings2, SlidersHorizontal, Droplets,
  Heading1, Heading2, Heading3, Link, RemoveFormatting, Minus
} from 'lucide-react';
import { Button } from '../components/ui/button';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

// Dynamic Field Categories for One-Click Insert Popup
const DYNAMIC_FIELD_CATEGORIES = [
  {
    category: 'Employee Master',
    icon: User,
    color: 'bg-blue-50 text-blue-700 border-blue-200',
    fields: [
      { label: 'Employee Name', tag: '{{EMPLOYEE_NAME}}', code: 'EMPLOYEE_NAME' },
      { label: 'Employee Code', tag: '{{EMPLOYEE_CODE}}', code: 'EMPLOYEE_CODE' },
      { label: 'Designation', tag: '{{DESIGNATION}}', code: 'DESIGNATION' },
      { label: 'Department', tag: '{{DEPARTMENT}}', code: 'DEPARTMENT' },
      { label: 'Date of Joining', tag: '{{DATE_OF_JOINING}}', code: 'DATE_OF_JOINING' },
      { label: 'Date of Birth', tag: '{{DATE_OF_BIRTH}}', code: 'DATE_OF_BIRTH' },
      { label: 'Gender', tag: '{{GENDER}}', code: 'GENDER' },
      { label: 'Blood Group', tag: '{{BLOOD_GROUP}}', code: 'BLOOD_GROUP' },
      { label: 'Marital Status', tag: '{{MARITAL_STATUS}}', code: 'MARITAL_STATUS' },
      { label: 'Work Location', tag: '{{WORK_LOCATION}}', code: 'WORK_LOCATION' },
      { label: 'Reporting Manager', tag: '{{REPORTING_MANAGER}}', code: 'REPORTING_MANAGER' },
      { label: 'Probation Period', tag: '{{PROBATION_PERIOD}}', code: 'PROBATION_PERIOD' },
      { label: 'Employment Type', tag: '{{EMPLOYMENT_TYPE}}', code: 'EMPLOYMENT_TYPE' },
      { label: 'Grade / Level', tag: '{{GRADE}}', code: 'GRADE' },
      { label: 'Mobile Number', tag: '{{MOBILE}}', code: 'MOBILE' },
      { label: 'Work Email', tag: '{{EMAIL}}', code: 'EMAIL' },
      { label: 'Current Address', tag: '{{ADDRESS}}', code: 'ADDRESS' },
    ]
  },
  {
    category: 'Company & HR',
    icon: Building2,
    color: 'bg-purple-50 text-purple-700 border-purple-200',
    fields: [
      { label: 'Company Name', tag: '{{COMPANY_NAME}}', code: 'COMPANY_NAME' },
      { label: 'Company Address', tag: '{{COMPANY_ADDRESS}}', code: 'COMPANY_ADDRESS' },
      { label: 'Company Email', tag: '{{COMPANY_EMAIL}}', code: 'COMPANY_EMAIL' },
      { label: 'Company Phone', tag: '{{COMPANY_PHONE}}', code: 'COMPANY_PHONE' },
      { label: 'HR Manager Name', tag: '{{HR_NAME}}', code: 'HR_NAME' },
      { label: 'HR Designation', tag: '{{HR_DESIGNATION}}', code: 'HR_DESIGNATION' },
      { label: 'Today Date', tag: '{{TODAY_DATE}}', code: 'TODAY_DATE' },
      { label: 'Reference Number', tag: '{{REF_NUMBER}}', code: 'REF_NUMBER' },
      { label: 'Current Year', tag: '{{CURRENT_YEAR}}', code: 'CURRENT_YEAR' },
    ]
  },
  {
    category: 'Salary & Compensation',
    icon: DollarSign,
    color: 'bg-emerald-50 text-emerald-700 border-emerald-200',
    fields: [
      { label: 'Annual CTC (₹)', tag: '{{ANNUAL_CTC}}', code: 'ANNUAL_CTC' },
      { label: 'Monthly CTC (₹)', tag: '{{MONTHLY_CTC}}', code: 'MONTHLY_CTC' },
      { label: 'Gross Salary (₹)', tag: '{{GROSS_SALARY}}', code: 'GROSS_SALARY' },
      { label: 'Basic Salary (₹)', tag: '{{BASIC_SALARY}}', code: 'BASIC_SALARY' },
      { label: 'Take Home Salary (₹)', tag: '{{TAKE_HOME_SALARY}}', code: 'TAKE_HOME_SALARY' },
      { label: 'Salary Annexure Table', tag: '{{ANNEXURE_TABLE}}', code: 'ANNEXURE_TABLE' },
    ]
  }
];

// Pre-built Policy Clauses Library
const CLAUSE_LIBRARY = [
  { key: 'probation', title: 'Probation & Confirmation Clause', category: 'Appointment', content: 'You will be on probation for a period of {{PROBATION_PERIOD}} from your date of joining. Upon satisfactory performance review, your services will be confirmed in writing.' },
  { key: 'notice_period', title: 'Notice Period & Resignation', category: 'Termination', content: 'During probation, either party may terminate employment by giving 30 days written notice. Post-confirmation, the required notice period shall be 60 days.' },
  { key: 'confidentiality', title: 'Confidentiality & Non-Disclosure (NDA)', category: 'Legal', content: 'You shall maintain absolute confidentiality regarding company software code, client records, trade secrets, financial data, and proprietary intellectual property during and after employment.' },
  { key: 'code_of_conduct', title: 'Code of Conduct & Ethics', category: 'Compliance', content: 'You agree to strictly adhere to all company policies, safety standards, workplace ethics, and IT security protocols issued by Human Resources from time to time.' },
  { key: 'transfer', title: 'Location & Transferability Clause', category: 'Appointment', content: 'Your initial posting is at {{WORK_LOCATION}}. However, the company reserves the right to transfer your services to any branch, department, subsidiary, or client location within India.' },
  { key: 'termination', title: 'Termination for Misconduct', category: 'Termination', content: 'Services are subject to immediate termination without notice in cases of gross misconduct, breach of confidentiality, fraud, or violation of company code of conduct.' },
  { key: 'working_hours', title: 'Working Hours & Attendance', category: 'Operations', content: 'Standard working hours are 9:00 AM to 6:00 PM, Monday through Saturday. Attendance must be logged daily via the PayrollPro Portal or biometric terminal.' },
  { key: 'leave_policy', title: 'Leave Entitlement & Policy', category: 'Operations', content: 'You will be eligible for Earned Leave, Casual Leave, and Sick Leave as per the company leave policy post confirmation.' }
];

// Available Salary Components Master
const ALL_SALARY_COMPONENTS = [
  { key: 'BASIC', label: 'Basic Salary', type: 'earning', defaultMonthly: 31250, defaultSection: 'earnings' },
  { key: 'DA', label: 'Dearness Allowance (DA)', type: 'earning', defaultMonthly: 0, defaultSection: 'earnings' },
  { key: 'HRA', label: 'House Rent Allowance (HRA)', type: 'earning', defaultMonthly: 15625, defaultSection: 'earnings' },
  { key: 'SPECIAL_ALLOWANCE', label: 'Special Allowance', type: 'earning', defaultMonthly: 9375, defaultSection: 'earnings' },
  { key: 'CONVEYANCE', label: 'Conveyance Allowance', type: 'earning', defaultMonthly: 1600, defaultSection: 'earnings' },
  { key: 'MEDICAL', label: 'Medical Allowance', type: 'earning', defaultMonthly: 1250, defaultSection: 'earnings' },
  { key: 'INCENTIVE', label: 'Performance Incentive', type: 'earning', defaultMonthly: 0, defaultSection: 'earnings' },
  { key: 'WASHING', label: 'Washing Allowance', type: 'earning', defaultMonthly: 0, defaultSection: 'earnings' },
  { key: 'FOOD', label: 'Food Allowance', type: 'earning', defaultMonthly: 0, defaultSection: 'earnings' },
  { key: 'ATTENDANCE', label: 'Attendance Allowance', type: 'earning', defaultMonthly: 0, defaultSection: 'earnings' },
  { key: 'OTHER_EARNINGS', label: 'Other Allowances', type: 'earning', defaultMonthly: 0, defaultSection: 'earnings' },
  { key: 'PF_EMPLOYEE', label: 'Employee PF Contribution (12%)', type: 'deduction', defaultMonthly: 1800, defaultSection: 'deductions' },
  { key: 'ESI_EMPLOYEE', label: 'Employee ESI Contribution (0.75%)', type: 'deduction', defaultMonthly: 0, defaultSection: 'deductions' },
  { key: 'PT', label: 'Professional Tax (PT)', type: 'deduction', defaultMonthly: 208, defaultSection: 'deductions' },
  { key: 'LWF_EMPLOYEE', label: 'Labour Welfare Fund (LWF)', type: 'deduction', defaultMonthly: 0, defaultSection: 'deductions' },
  { key: 'TDS', label: 'Income Tax (TDS)', type: 'deduction', defaultMonthly: 0, defaultSection: 'deductions' },
  { key: 'ADVANCE_RECOVERY', label: 'Advance Recovery', type: 'deduction', defaultMonthly: 0, defaultSection: 'deductions' },
  { key: 'LOAN_RECOVERY', label: 'Loan Recovery', type: 'deduction', defaultMonthly: 0, defaultSection: 'deductions' },
  { key: 'OTHER_DEDUCTIONS', label: 'Other Deductions', type: 'deduction', defaultMonthly: 0, defaultSection: 'deductions' },
  { key: 'PF_EMPLOYER', label: 'Employer PF Contribution (12%)', type: 'employer', defaultMonthly: 1800, defaultSection: 'employer_perks' },
  { key: 'ESI_EMPLOYER', label: 'Employer ESI Contribution (3.25%)', type: 'employer', defaultMonthly: 0, defaultSection: 'employer_perks' },
  { key: 'BONUS', label: 'Statutory Bonus / Ex-Gratia', type: 'employer', defaultMonthly: 2000, defaultSection: 'employer_perks' },
  { key: 'LEAVE_ENCASHMENT', label: 'Leave Encashment Provision', type: 'employer', defaultMonthly: 0, defaultSection: 'employer_perks' },
  { key: 'GRATUITY_ACCRUAL', label: 'Gratuity Provision (4.81%)', type: 'employer', defaultMonthly: 1500, defaultSection: 'employer_perks' },
  { key: 'MOBILE_REIMBURSEMENT', label: 'Mobile & Internet Allowance', type: 'employer', defaultMonthly: 0, defaultSection: 'employer_perks' },
  { key: 'FUEL_ALLOWANCE', label: 'Fuel & Conveyance Reimbursement', type: 'employer', defaultMonthly: 0, defaultSection: 'employer_perks' },
  { key: 'HEALTH_INSURANCE', label: 'Medical & Life Insurance', type: 'employer', defaultMonthly: 500, defaultSection: 'employer_perks' },
  { key: 'RETENTION_BONUS', label: 'Retention Bonus', type: 'employer', defaultMonthly: 0, defaultSection: 'employer_perks' },
  { key: 'OTHER_BENEFITS', label: 'Other Employer Benefits', type: 'employer', defaultMonthly: 0, defaultSection: 'employer_perks' }
];

// Annexure Layout Presets
const ANNEXURE_PRESETS = [
  {
    id: 'traditional_breakup',
    name: 'Traditional Salary Breakup',
    description: 'Corporate sectional format: Earnings, Deductions, Employer Perks, Take-Home & Fixed CTC bottom line.',
    sections: [
      {
        id: 'earnings',
        heading: 'A. Salary & Allowances',
        category_type: 'earning',
        show_subtotal: true,
        subtotal_label: 'TOTAL GROSS SALARY (A)',
        components: [
          { head_code: 'BASIC', display_label: 'Basic + DA', visible: true, display_order: 1 },
          { head_code: 'HRA', display_label: 'House Rent Allowance (HRA)', visible: true, display_order: 2 },
          { head_code: 'CONVEYANCE', display_label: 'Conveyance Allowance', visible: true, display_order: 3 },
          { head_code: 'MEDICAL', display_label: 'Medical Allowance', visible: true, display_order: 4 },
          { head_code: 'SPECIAL_ALLOWANCE', display_label: 'Special Allowance', visible: true, display_order: 5 },
        ]
      },
      {
        id: 'deductions',
        heading: 'Deductions',
        category_type: 'deduction',
        show_subtotal: true,
        subtotal_label: 'TOTAL EMPLOYEE DEDUCTIONS (B)',
        components: [
          { head_code: 'PF_EMPLOYEE', display_label: 'PF Employee', visible: true, display_order: 1 },
          { head_code: 'ESI_EMPLOYEE', display_label: 'ESI Employee', visible: true, display_order: 2 },
          { head_code: 'PT', display_label: 'Professional Tax', visible: true, display_order: 3 },
          { head_code: 'LWF_EMPLOYEE', display_label: 'Labour Welfare Fund', visible: true, display_order: 4 },
        ]
      },
      {
        id: 'employer_perks',
        heading: 'B. Employer Contribution / Perks',
        category_type: 'employer',
        show_subtotal: true,
        subtotal_label: 'TOTAL PERKS & EMPLOYER COST (C)',
        components: [
          { head_code: 'PF_EMPLOYER', display_label: 'Employer PF', visible: true, display_order: 1 },
          { head_code: 'ESI_EMPLOYER', display_label: 'Employer ESI', visible: true, display_order: 2 },
          { head_code: 'BONUS', display_label: 'Statutory Bonus', visible: true, display_order: 3 },
          { head_code: 'GRATUITY_ACCRUAL', display_label: 'Gratuity Provision', visible: true, display_order: 4 },
        ]
      }
    ]
  },
  {
    id: 'corporate_table',
    name: 'Corporate Table (Current)',
    description: 'Standard 3-part layout separating Earnings, Deductions, and Employer Contributions.',
    sections: [
      { id: 'earnings', heading: 'A. EARNINGS & ALLOWANCES', category_type: 'earning', show_subtotal: true, subtotal_label: 'TOTAL GROSS SALARY (A)', components: [] },
      { id: 'deductions', heading: 'B. EMPLOYEE DEDUCTIONS', category_type: 'deduction', show_subtotal: true, subtotal_label: 'TOTAL DEDUCTIONS (B)', components: [] },
      { id: 'employer', heading: 'C. EMPLOYER CONTRIBUTIONS', category_type: 'employer', show_subtotal: true, subtotal_label: 'TOTAL EMPLOYER COST (C)', components: [] }
    ]
  },
  {
    id: 'detailed_ctc',
    name: 'Detailed CTC Breakup',
    description: 'Comprehensive CTC breakdown including Fixed Earnings, Variable Pay, and Retirals.',
    sections: [
      { id: 'fixed_earnings', heading: '1. Fixed Structural Earnings', category_type: 'earning', show_subtotal: true, subtotal_label: 'TOTAL FIXED EARNINGS', components: [] },
      { id: 'variable_pay', heading: '2. Performance Incentives & Bonuses', category_type: 'earning', show_subtotal: true, subtotal_label: 'TOTAL VARIABLE PAY', components: [] },
      { id: 'statutory_benefits', heading: '3. Statutory Retirals & Benefits', category_type: 'employer', show_subtotal: true, subtotal_label: 'TOTAL STATUTORY COST', components: [] },
      { id: 'deductions', heading: '4. Employee Statutory Deductions', category_type: 'deduction', show_subtotal: true, subtotal_label: 'TOTAL DEDUCTIONS', components: [] }
    ]
  },
  {
    id: 'manufacturing',
    name: 'Manufacturing Salary Format',
    description: 'Factory/Plant format with Attendance Allowances, Shift Allowances, Washing, and LWF.',
    sections: [
      { id: 'plant_wages', heading: 'Plant Wage & Basic Pay', category_type: 'earning', show_subtotal: true, subtotal_label: 'GROSS PLANT WAGES', components: [] },
      { id: 'plant_deductions', heading: 'Statutory Factory Deductions', category_type: 'deduction', show_subtotal: true, subtotal_label: 'TOTAL DEDUCTIONS', components: [] },
      { id: 'plant_perks', heading: 'Employer Welfare & Canteen Provisions', category_type: 'employer', show_subtotal: true, subtotal_label: 'TOTAL EMPLOYER PERKS', components: [] }
    ]
  },
  {
    id: 'custom_builder',
    name: 'Custom Annexure Builder',
    description: 'Fully customizable layout with user-defined sections, component ordering, and formula rows.',
    sections: [
      { id: 'custom_sec_1', heading: 'Custom Salary Section 1', category_type: 'earning', show_subtotal: true, subtotal_label: 'SECTION 1 TOTAL', components: [] }
    ]
  }
];

// Preset Themes for Template Styling
const TEMPLATE_THEMES = [
  { id: 'corporate_blue', name: 'Corporate Blue', primaryColor: '#0052FF', secondaryColor: '#0F172A', font: 'Inter' },
  { id: 'modern_slate', name: 'Modern Slate', primaryColor: '#0F172A', secondaryColor: '#475569', font: 'Inter' },
  { id: 'executive_gold', name: 'Executive Gold', primaryColor: '#B45309', secondaryColor: '#1E293B', font: 'Georgia' },
  { id: 'minimal_clean', name: 'Minimalist Clean', primaryColor: '#2563EB', secondaryColor: '#334155', font: 'Roboto' },
  { id: 'government_formal', name: 'Government Formal', primaryColor: '#15803D', secondaryColor: '#14532D', font: 'Times New Roman' }
];

// Sample Live Data for A4 Preview Rendering
const SAMPLE_DATA = {
  COMPANY_NAME: 'Hubert Enviro Care Systems Pvt Ltd',
  COMPANY_ADDRESS: 'No. 12, Industrial Estate, Guindy, Chennai - 600032',
  EMPLOYEE_NAME: 'Abraham Abishek Moses',
  EMPLOYEE_CODE: 'M002',
  DESIGNATION: 'Senior Software Engineer',
  DEPARTMENT: 'Information Technology',
  LOCATION: 'Chennai HQ',
  WORK_LOCATION: 'Chennai HQ',
  DATE_OF_JOINING: '01 August 2026',
  DATE_OF_BIRTH: '15 June 1995',
  REPORTING_MANAGER: 'Dr. J. R. Sharma',
  PROBATION_PERIOD: '6 Months',
  EMPLOYMENT_TYPE: 'Full-Time Permanent',
  GRADE: 'Level 3 - Senior',
  EMAIL: 'abraham.m@hubertenviro.com',
  MOBILE: '+91 98765 43210',
  ADDRESS: 'Flat 4A, Green Acres Apartments, Velachery, Chennai - 600042',
  ANNUAL_CTC: '₹ 7,50,000.00',
  MONTHLY_CTC: '₹ 62,500.00',
  GROSS_SALARY: '₹ 56,250.00',
  BASIC_SALARY: '₹ 31,250.00',
  TAKE_HOME_SALARY: '₹ 49,850.00',
  HR_NAME: 'K. S. Sundaram',
  HR_DESIGNATION: 'Head of Human Resources',
  TODAY_DATE: new Date().toLocaleDateString('en-GB', { day: '2-digit', month: 'long', year: 'numeric' }),
  REF_NUMBER: 'APPT/M002/2026',
  CURRENT_YEAR: String(new Date().getFullYear()),
};

// HTML Normalizer Helper: Strips MS Office bloated styles, margins, padding & empty paragraphs
const cleanPastedHtml = (rawHtml) => {
  if (!rawHtml) return '';

  let cleaned = rawHtml.replace(/<!--[\s\S]*?-->/gi, '');
  cleaned = cleaned.replace(/<o:p[\s\S]*?>[\s\S]*?<\/o:p>/gi, '');
  cleaned = cleaned.replace(/<\/?(xml|meta|link|style|title|applet|script|iframe)[^>]*>/gi, '');

  const parser = new DOMParser();
  const doc = parser.parseFromString(cleaned, 'text/html');

  const sanitizeNode = (node) => {
    if (node.nodeType === Node.ELEMENT_NODE) {
      const tag = node.tagName.toLowerCase();

      // Remove Mso & Office classes
      if (node.hasAttribute('class')) {
        const cls = node.getAttribute('class') || '';
        if (/mso|Office|Word/i.test(cls)) {
          node.removeAttribute('class');
        }
      }

      // Remove inline margin & padding that distort paragraph spacing
      if (node.hasAttribute('style')) {
        let style = node.getAttribute('style') || '';
        style = style.replace(/margin(-[a-z]+)?:\s*[^;]+;?/gi, '');
        style = style.replace(/padding(-[a-z]+)?:\s*[^;]+;?/gi, '');
        style = style.replace(/mso-[^;]+;?/gi, '');
        style = style.replace(/line-height:\s*[^;]+;?/gi, '');
        if (style.trim()) {
          node.setAttribute('style', style.trim());
        } else {
          node.removeAttribute('style');
        }
      }

      // Unwrap empty redundant spans
      if ((tag === 'span' || tag === 'font') && !node.attributes.length) {
        const parent = node.parentNode;
        if (parent) {
          while (node.firstChild) {
            parent.insertBefore(node.firstChild, node);
          }
          parent.removeChild(node);
          return;
        }
      }

      Array.from(node.childNodes).forEach(sanitizeNode);
    }
  };

  sanitizeNode(doc.body);

  let html = doc.body.innerHTML;
  html = html.replace(/(<p>\s*(<br\s*\/?>|&nbsp;|\s)*<\/p>){2,}/gi, '<p><br></p>');
  html = html.replace(/(<br\s*\/?>\s*){3,}/gi, '<br/><br/>');

  return html.trim();
};

export default function DocumentTemplatesMaster() {
  const [companies, setCompanies] = useState([]);
  const [selectedCompany, setSelectedCompany] = useActiveCompany();
  const [catalog, setCatalog] = useState([]);
  const [activeDocType, setActiveDocType] = useState('appointment_order');
  const [templates, setTemplates] = useState([]);
  const [policies, setPolicies] = useState([]);
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('templates'); // 'templates' | 'policies'

  // View Mode: 'list' | 'wizard' | 'designer'
  const [viewMode, setViewMode] = useState('list');

  // Zoom Level for A4 Canvas (50% to 200%)
  const [zoomLevel, setZoomLevel] = useState(100);

  // Active Inspector Tab in Right Panel: 'properties' | 'watermark' | 'salary'
  const [inspectorTab, setInspectorTab] = useState('properties');

  // WYSIWYG ContentEditable Editor Ref
  const editorRef = useRef(null);

  // MS Word Designer State
  const [designerState, setDesignerState] = useState({
    id: null,
    template_name: 'Standard Executive Appointment Order',
    doc_type: 'appointment_order',
    version: 1,
    is_default: true,
    effective_from: new Date().toISOString().split('T')[0],
    description: 'Corporate appointment order template.',
    
    // Page Setup
    pageSetup: { paperSize: 'A4', orientation: 'portrait', marginTop: 20, marginBottom: 20, marginLeft: 20, marginRight: 20 },
    
    // Theme & Typography
    theme: { preset: 'corporate_blue', primaryColor: '#0052FF', secondaryColor: '#0F172A', fontFamily: 'Inter', fontSize: '11pt', lineHeight: '1.5' },

    // Watermark Settings
    watermark: { enabled: false, type: 'text', text: 'CONFIDENTIAL', opacity: 15, rotation: 45, color: '#94A3B8' },

    // Salary Annexure Configuration
    annexureConfig: {
      layout_type: 'traditional_breakup',
      sections: [
        {
          id: 'earnings',
          heading: 'A. Salary & Allowances',
          category_type: 'earning',
          show_subtotal: true,
          subtotal_label: 'TOTAL GROSS SALARY (A)',
          components: [
            { head_code: 'BASIC', display_label: 'Basic + DA', visible: true, display_order: 1 },
            { head_code: 'HRA', display_label: 'House Rent Allowance (HRA)', visible: true, display_order: 2 },
            { head_code: 'CONVEYANCE', display_label: 'Conveyance Allowance', visible: true, display_order: 3 },
            { head_code: 'MEDICAL', display_label: 'Medical Allowance', visible: true, display_order: 4 },
            { head_code: 'SPECIAL_ALLOWANCE', display_label: 'Special Allowance', visible: true, display_order: 5 },
          ]
        },
        {
          id: 'deductions',
          heading: 'Deductions',
          category_type: 'deduction',
          show_subtotal: true,
          subtotal_label: 'TOTAL EMPLOYEE DEDUCTIONS (B)',
          components: [
            { head_code: 'PF_EMPLOYEE', display_label: 'PF Employee', visible: true, display_order: 1 },
            { head_code: 'ESI_EMPLOYEE', display_label: 'ESI Employee', visible: true, display_order: 2 },
            { head_code: 'PT', display_label: 'Professional Tax', visible: true, display_order: 3 },
            { head_code: 'LWF_EMPLOYEE', display_label: 'Labour Welfare Fund', visible: true, display_order: 4 },
          ]
        },
        {
          id: 'employer_perks',
          heading: 'B. Employer Contribution / Perks',
          category_type: 'employer',
          show_subtotal: true,
          subtotal_label: 'TOTAL PERKS & EMPLOYER COST (C)',
          components: [
            { head_code: 'PF_EMPLOYER', display_label: 'Employer PF', visible: true, display_order: 1 },
            { head_code: 'ESI_EMPLOYER', display_label: 'Employer ESI', visible: true, display_order: 2 },
            { head_code: 'BONUS', display_label: 'Statutory Bonus', visible: true, display_order: 3 },
            { head_code: 'GRATUITY_ACCRUAL', display_label: 'Gratuity Provision', visible: true, display_order: 4 },
          ]
        }
      ],
      columns: { monthly: true, annual: true, category: true, sl_no: false, percentage: false, remarks: false },
      formatting: { primary_color: '#0052FF', font_family: 'Inter', font_size: 'medium', alternate_row_colors: true, currency_symbol: '₹', decimal_places: 2 }
    },

    // Salary Components Panel Config
    salaryComponents: [
      { key: 'BASIC', label: 'Basic + DA', type: 'earning', monthly: 31250, enabled: true },
      { key: 'HRA', label: 'House Rent Allowance (HRA)', type: 'earning', monthly: 15625, enabled: true },
      { key: 'SPECIAL_ALLOWANCE', label: 'Special Allowance', type: 'earning', monthly: 9375, enabled: true },
      { key: 'PF_EMPLOYER', label: 'Employer PF Contribution', type: 'employer', monthly: 1800, enabled: true },
      { key: 'PF_EMPLOYEE', label: 'Employee PF Deduction', type: 'deduction', monthly: 1800, enabled: true },
      { key: 'PT', label: 'Professional Tax (PT)', type: 'deduction', monthly: 208, enabled: true }
    ],

    // Active Section Selection
    activeSectionId: 'body',

    // Document Structure Sections (rich HTML inside body text)
    sections: [
      { id: 'header', type: 'header', title: 'Company Header', enabled: true },
      { id: 'ref_date', type: 'ref_date', title: 'Ref No & Date', enabled: true },
      { id: 'recipient', type: 'recipient', title: 'Recipient Details', enabled: true },
      { id: 'subject', type: 'subject', title: 'Subject Line', enabled: true, text: 'SUBJECT: APPOINTMENT ORDER & TERMS OF EMPLOYMENT', align: 'center', bold: true },
      { id: 'greeting', type: 'greeting', title: 'Salutation', enabled: true, text: 'Dear {{EMPLOYEE_NAME}},' },
      {
        id: 'body',
        type: 'body',
        title: 'Document Body Content',
        enabled: true,
        text: `<p>We are pleased to offer you an appointment with <strong>{{COMPANY_NAME}}</strong> as <strong>{{DESIGNATION}}</strong> in the <strong>{{DEPARTMENT}}</strong> department. We welcome you to our organization and look forward to a successful association.</p><p>Your date of joining will be <strong>{{DATE_OF_JOINING}}</strong> at our <strong>{{WORK_LOCATION}}</strong> office. You will report to <strong>{{REPORTING_MANAGER}}</strong>.</p><p>Your Cost to Company (CTC) will be <strong>{{ANNUAL_CTC}}</strong> per annum. The detailed monthly and annual salary breakup is outlined in Annexure I below.</p>`
      },
      { id: 'terms', type: 'terms', title: 'Terms & Conditions', enabled: true, clauses: ['probation', 'notice_period', 'confidentiality', 'code_of_conduct', 'transfer'] },
      { id: 'salary_table', type: 'salary_table', title: 'Salary Annexure Table', enabled: true },
      { id: 'signature', type: 'signature', title: 'Signatures Block', enabled: true },
      { id: 'footer', type: 'footer', title: 'Document Footer', enabled: true }
    ]
  });

  // Modal States
  const [showFieldPicker, setShowFieldPicker] = useState(false);
  const [showPolicyModal, setShowPolicyModal] = useState(false);
  const [policyForm, setPolicyForm] = useState({ policy_key: 'probation', title: '', content: '' });

  useEffect(() => {
    axios.get(`${API_URL}/companies`, { withCredentials: true })
      .then(({ data }) => {
        setCompanies(data);
        setSelectedCompany((cur) => (data.length > 0 && !cur ? data[0].id : cur));
      })
      .catch(() => {});

    axios.get(`${API_URL}/documents/catalog`, { withCredentials: true })
      .then(({ data }) => setCatalog(data.doc_types || []))
      .catch(() => {});
  }, [setSelectedCompany]);

  const fetchTemplates = useCallback(async () => {
    if (!selectedCompany) return;
    setLoading(true);
    try {
      const { data } = await axios.get(`${API_URL}/documents/templates?company_id=${selectedCompany}&doc_type=${activeDocType}`, { withCredentials: true });
      setTemplates(data);
    } catch (e) {
      toast.error('Failed to load document templates');
    }
    setLoading(false);
  }, [selectedCompany, activeDocType]);

  const fetchPolicies = useCallback(async () => {
    if (!selectedCompany) return;
    try {
      const { data } = await axios.get(`${API_URL}/documents/policies?company_id=${selectedCompany}`, { withCredentials: true });
      setPolicies(data.clauses || []);
    } catch (e) {
      console.error(e);
    }
  }, [selectedCompany]);

  useEffect(() => {
    if (selectedCompany) {
      fetchTemplates();
      fetchPolicies();
    }
  }, [selectedCompany, fetchTemplates, fetchPolicies]);

  // Populate contentEditable canvas ONCE when entering designer or switching template ID
  useEffect(() => {
    if (viewMode === 'designer' && editorRef.current) {
      const bodySec = designerState.sections.find(s => s.id === 'body');
      const targetHtml = bodySec?.text || '<p>Type here...</p>';
      if (editorRef.current.innerHTML !== targetHtml) {
        editorRef.current.innerHTML = targetHtml;
      }
    }
  }, [viewMode, designerState.id]);

  // Execute Rich Text Command on ContentEditable Canvas
  const execCmd = (command, value = null) => {
    document.execCommand(command, false, value);
    if (editorRef.current) {
      const html = cleanPastedHtml(editorRef.current.innerHTML);
      setDesignerState(prev => ({
        ...prev,
        sections: prev.sections.map(s => s.id === 'body' ? { ...s, text: html } : s)
      }));
    }
  };

  // Launch MS Word Designer for New Template
  const handleOpenDesignerNew = () => {
    const docLabel = catalog.find(c => c.type === activeDocType)?.label || 'Appointment Order';
    setDesignerState({
      id: null,
      template_name: `Standard ${docLabel} Template`,
      doc_type: activeDocType,
      version: 1,
      is_default: templates.length === 0,
      effective_from: new Date().toISOString().split('T')[0],
      description: `Official corporate ${docLabel.toLowerCase()} built with Word Designer.`,
      pageSetup: { paperSize: 'A4', orientation: 'portrait', marginTop: 20, marginBottom: 20, marginLeft: 20, marginRight: 20 },
      theme: { preset: 'corporate_blue', primaryColor: '#0052FF', secondaryColor: '#0F172A', fontFamily: 'Inter', fontSize: '11pt', lineHeight: '1.5' },
      watermark: { enabled: false, type: 'text', text: 'CONFIDENTIAL', opacity: 15, rotation: 45, color: '#94A3B8' },
      salaryComponents: [
        { key: 'basic', label: 'Basic Salary', type: 'earning', monthly: 31250, enabled: true },
        { key: 'hra', label: 'House Rent Allowance (HRA)', type: 'earning', monthly: 15625, enabled: true },
        { key: 'special', label: 'Special Allowance', type: 'earning', monthly: 9375, enabled: true },
        { key: 'pf_employer', label: 'Employer PF Contribution', type: 'employer', monthly: 1800, enabled: true },
        { key: 'pf_employee', label: 'Employee PF Deduction', type: 'deduction', monthly: 1800, enabled: true },
        { key: 'pt', label: 'Professional Tax (PT)', type: 'deduction', monthly: 208, enabled: true }
      ],
      activeSectionId: 'body',
      sections: [
        { id: 'header', type: 'header', title: 'Company Header', enabled: true },
        { id: 'ref_date', type: 'ref_date', title: 'Ref No & Date', enabled: true },
        { id: 'recipient', type: 'recipient', title: 'Recipient Details', enabled: true },
        { id: 'subject', type: 'subject', title: 'Subject Line', enabled: true, text: 'SUBJECT: LETTER OF APPOINTMENT', align: 'center', bold: true },
        { id: 'greeting', type: 'greeting', title: 'Salutation', enabled: true, text: 'Dear {{EMPLOYEE_NAME}},' },
        {
          id: 'body',
          type: 'body',
          title: 'Document Body Content',
          enabled: true,
          text: `<p>We are pleased to offer you an appointment with <strong>{{COMPANY_NAME}}</strong> as <strong>{{DESIGNATION}}</strong> in the <strong>{{DEPARTMENT}}</strong> department. We welcome you to our organization and look forward to a successful association.</p><p>Your date of joining will be <strong>{{DATE_OF_JOINING}}</strong> at our <strong>{{WORK_LOCATION}}</strong> office. You will report to <strong>{{REPORTING_MANAGER}}</strong>.</p><p>Your Cost to Company (CTC) will be <strong>{{ANNUAL_CTC}}</strong> per annum. The detailed monthly and annual salary breakup is outlined in Annexure I below.</p>`
        },
        { id: 'terms', type: 'terms', title: 'Terms & Conditions', enabled: true, clauses: ['probation', 'notice_period', 'confidentiality', 'code_of_conduct'] },
        { id: 'salary_table', type: 'salary_table', title: 'Salary Annexure Table', enabled: true },
        { id: 'signature', type: 'signature', title: 'Signatures Block', enabled: true },
        { id: 'footer', type: 'footer', title: 'Document Footer', enabled: true }
      ]
    });
    setViewMode('designer');
  };

  // Launch MS Word Designer to Edit Existing Template
  const handleEditTemplate = (tpl) => {
    const clauses = tpl.terms_clauses || ['probation', 'notice_period', 'confidentiality'];
    let bodyTxt = cleanPastedHtml(tpl.body_template || '');
    if (!bodyTxt.includes('<p>') && !bodyTxt.includes('<div>')) {
      bodyTxt = bodyTxt.split('\n\n').map(p => `<p>${p.replace(/\n/g, '<br/>')}</p>`).join('');
    }
    const defaultAnnexureCfg = ANNEXURE_PRESETS[0];
    setDesignerState(prev => ({
      ...prev,
      id: tpl.id || tpl._id,
      template_name: tpl.template_name || '',
      doc_type: tpl.doc_type || activeDocType,
      version: tpl.version || 1,
      is_default: !!tpl.is_default,
      effective_from: tpl.effective_from || new Date().toISOString().split('T')[0],
      description: tpl.description || '',
      annexureConfig: tpl.annexure_config || prev.annexureConfig || defaultAnnexureCfg,
      activeSectionId: 'body',
      sections: prev.sections.map(s => {
        if (s.id === 'body') return { ...s, text: bodyTxt || s.text };
        if (s.id === 'terms') return { ...s, clauses };
        return s;
      })
    }));
    setViewMode('designer');
  };

  // Section Manipulation Helpers
  const toggleSectionEnabled = (secId) => {
    setDesignerState(prev => ({
      ...prev,
      sections: prev.sections.map(s => s.id === secId ? { ...s, enabled: !s.enabled } : s)
    }));
  };

  const moveSection = (index, direction) => {
    const newSections = [...designerState.sections];
    const targetIdx = index + direction;
    if (targetIdx < 0 || targetIdx >= newSections.length) return;
    const temp = newSections[index];
    newSections[index] = newSections[targetIdx];
    newSections[targetIdx] = temp;
    setDesignerState(prev => ({ ...prev, sections: newSections }));
  };

  // Insert Dynamic Field Chip into Body Content
  const handleInsertFieldChip = (fieldObj) => {
    if (editorRef.current) {
      editorRef.current.focus();
      execCmd('insertHTML', ` <strong>${fieldObj.tag}</strong> `);
    } else {
      setDesignerState(prev => ({
        ...prev,
        sections: prev.sections.map(s => s.id === 'body' ? { ...s, text: (s.text || '') + ` <strong>${fieldObj.tag}</strong> ` } : s)
      }));
    }
    setShowFieldPicker(false);
    toast.success(`Inserted merge field [${fieldObj.label}]`);
  };

  // Calculated Dynamic Salary Totals
  const calculatedSalaryTotals = useMemo(() => {
    const enabledComps = designerState.salaryComponents.filter(c => c.enabled);
    const grossMonthly = enabledComps.filter(c => c.type === 'earning').reduce((acc, curr) => acc + (curr.monthly || 0), 0);
    const employerMonthly = enabledComps.filter(c => c.type === 'employer').reduce((acc, curr) => acc + (curr.monthly || 0), 0);
    const deductionsMonthly = enabledComps.filter(c => c.type === 'deduction').reduce((acc, curr) => acc + (curr.monthly || 0), 0);
    const ctcMonthly = grossMonthly + employerMonthly;
    const takeHomeMonthly = grossMonthly - deductionsMonthly;

    return {
      grossMonthly,
      grossAnnual: grossMonthly * 12,
      ctcMonthly,
      ctcAnnual: ctcMonthly * 12,
      takeHomeMonthly,
      takeHomeAnnual: takeHomeMonthly * 12,
    };
  }, [designerState.salaryComponents]);

  // ================================================================
  // DYNAMIC PAGINATION ENGINE (LINE SPLITTING & ZERO WHITE SPACE)
  // ================================================================
  const dynamicPages = useMemo(() => {
    const pages = [];
    let currentPage = [];
    let currentHeight = 0;

    const getPageCapacity = (pageIndex) => {
      return pageIndex === 0 ? 760 : 860;
    };

    let activeCapacity = getPageCapacity(0);

    const pushItem = (item, height) => {
      currentPage.push(item);
      currentHeight += height;
    };

    const startNewPage = () => {
      if (currentPage.length > 0) {
        pages.push(currentPage);
      }
      currentPage = [];
      currentHeight = 0;
      activeCapacity = getPageCapacity(pages.length);
    };

    const enabledSections = designerState.sections.filter(s => s.enabled);

    enabledSections.forEach(sec => {
      if (sec.id === 'header') {
        if (currentHeight + 110 > activeCapacity) startNewPage();
        pushItem({ type: 'header' }, 110);
      } else if (sec.id === 'ref_date') {
        if (currentHeight + 35 > activeCapacity) startNewPage();
        pushItem({ type: 'ref_date' }, 35);
      } else if (sec.id === 'recipient') {
        if (currentHeight + 80 > activeCapacity) startNewPage();
        pushItem({ type: 'recipient' }, 80);
      } else if (sec.id === 'subject') {
        if (currentHeight + 45 > activeCapacity) startNewPage();
        pushItem({ type: 'subject', text: sec.text }, 45);
      } else if (sec.id === 'greeting') {
        if (currentHeight + 30 > activeCapacity) startNewPage();
        pushItem({ type: 'greeting', text: sec.text }, 30);
      } else if (sec.id === 'body') {
        const rawHtml = sec.text || '';
        const paragraphs = rawHtml.split(/<\/p>|<br\s*\/?>/i).filter(p => p.replace(/<[^>]*>/g, '').trim());

        paragraphs.forEach(p => {
          const cleanText = p.replace(/<[^>]*>/g, '');
          const isHeading = cleanText.length < 50 || /^\d+\.|\b(Subject|Dear|To|Ref)\b/i.test(cleanText);
          const totalLines = Math.max(1, Math.ceil(cleanText.length / 75));
          const pHeight = totalLines * 20 + 8;

          const remainingSpace = activeCapacity - currentHeight;

          if (pHeight <= remainingSpace) {
            pushItem({ type: 'body_paragraph', html: p + '</p>' }, pHeight);
          } else {
            // Check if we can fit part of the paragraph on current page
            const linesThatFit = Math.floor((remainingSpace - 10) / 20);

            if (linesThatFit >= 2 && !isHeading) {
              // Split paragraph text across pages
              const charSplitIdx = linesThatFit * 75;
              const textPart1 = cleanText.slice(0, charSplitIdx);
              const textPart2 = cleanText.slice(charSplitIdx);

              const heightPart1 = linesThatFit * 20 + 6;
              const heightPart2 = (totalLines - linesThatFit) * 20 + 8;

              pushItem({ type: 'body_paragraph', html: `<p>${textPart1}</p>` }, heightPart1);
              startNewPage();
              pushItem({ type: 'body_paragraph', html: `<p>${textPart2}</p>` }, heightPart2);
            } else {
              // Heading Orphan Protection: If last item on current page was a heading, move it to next page
              if (currentPage.length > 0) {
                const lastItem = currentPage[currentPage.length - 1];
                if (lastItem && lastItem.type === 'body_paragraph') {
                  const lastClean = (lastItem.html || '').replace(/<[^>]*>/g, '');
                  if (lastClean.length < 50 || /^\d+\./.test(lastClean)) {
                    const headingItem = currentPage.pop();
                    currentHeight -= (Math.ceil(lastClean.length / 75) * 20 + 8);
                    startNewPage();
                    pushItem(headingItem, Math.ceil(lastClean.length / 75) * 20 + 8);
                  } else {
                    startNewPage();
                  }
                } else {
                  startNewPage();
                }
              } else {
                startNewPage();
              }
              pushItem({ type: 'body_paragraph', html: p + '</p>' }, pHeight);
            }
          }
        });
      } else if (sec.id === 'terms') {
        if (currentHeight + 35 > activeCapacity) startNewPage();
        pushItem({ type: 'terms_title' }, 35);
        const clauses = sec.clauses || [];
        clauses.forEach(k => {
          const cl = CLAUSE_LIBRARY.find(c => c.key === k);
          if (cl) {
            const lines = Math.max(1, Math.ceil(cl.content.length / 80));
            const cHeight = lines * 20 + 18;
            if (currentHeight + cHeight > activeCapacity) startNewPage();
            pushItem({ type: 'terms_clause', clause: cl }, cHeight);
          }
        });
      } else if (sec.id === 'salary_table') {
        // KEEP TOGETHER RULE: Salary Annexure Table
        if (currentHeight + 260 > activeCapacity) startNewPage();
        pushItem({ type: 'salary_table' }, 260);
      } else if (sec.id === 'signature') {
        // KEEP TOGETHER RULE: Signature Block & Seal
        if (currentHeight + 160 > activeCapacity) startNewPage();
        pushItem({ type: 'signature' }, 160);
      }
    });

    if (currentPage.length > 0) {
      pages.push(currentPage);
    }

    return pages.length > 0 ? pages : [[]];
  }, [designerState.sections, designerState.salaryComponents]);

  // Save Template to Backend with Sanitized Clean HTML and Annexure Config
  const handleSaveDesignerTemplate = async () => {
    const bodySec = designerState.sections.find(s => s.id === 'body');
    const termsSec = designerState.sections.find(s => s.id === 'terms');

    const cleanBodyHtml = cleanPastedHtml(bodySec?.text || '');

    const payload = {
      company_id: selectedCompany,
      doc_type: designerState.doc_type,
      template_name: designerState.template_name,
      description: designerState.description,
      effective_from: designerState.effective_from,
      is_default: designerState.is_default,
      body_template: cleanBodyHtml,
      terms_clauses: termsSec?.clauses || [],
      annexure_config: designerState.annexureConfig || {},
    };

    try {
      if (designerState.id) {
        await axios.put(`${API_URL}/documents/templates/${designerState.id}`, payload, { withCredentials: true });
        toast.success('Document template updated!');
      } else {
        await axios.post(`${API_URL}/documents/templates`, payload, { withCredentials: true });
        toast.success('Visual MS Word Template saved!');
      }
      setViewMode('list');
      fetchTemplates();
    } catch (err) {
      toast.error('Failed to save template');
    }
  };

  // Render Formatted HTML Content with Interactive Dynamic Chips in Live Preview
  const renderFormattedHtmlWithChips = (htmlStr) => {
    if (!htmlStr) return null;
    let resolved = htmlStr;
    Object.entries(SAMPLE_DATA).forEach(([k, v]) => {
      const chipHtml = `<span class="inline-flex items-center gap-1 bg-blue-100 text-blue-900 border border-blue-300 rounded px-1.5 py-0.5 font-mono text-[11px] font-bold mx-0.5 shadow-2xs">${v}</span>`;
      const reg = new RegExp(`\\{\\{\\s*${k}\\s*\\}\\}`, 'gi');
      resolved = resolved.replace(reg, chipHtml);
    });
    resolved = resolved.replace(/\{\{\s*[A-Z0-9_]+\s*\}\}/gi, '');
    return <div dangerouslySetInnerHTML={{ __html: resolved }} className="editor-html-container text-slate-800 font-sans text-xs" />;
  };

  const activeSection = designerState.sections.find(s => s.id === designerState.activeSectionId) || designerState.sections[0];

  return (
    <MainLayout>
      <div className="space-y-6 p-6" data-testid="document-templates-page">
        {/* Global Styles for Single Source of Truth Paragraph & List Spacing */}
        <style>{`
          .editor-html-container p, .word-editor-canvas p {
            margin-top: 0px !important;
            margin-bottom: 10px !important;
            line-height: 1.5 !important;
            padding: 0px !important;
          }
          .editor-html-container ul, .word-editor-canvas ul,
          .editor-html-container ol, .word-editor-canvas ol {
            margin-top: 0px !important;
            margin-bottom: 10px !important;
            padding-left: 20px !important;
          }
          .editor-html-container li, .word-editor-canvas li {
            margin-bottom: 3px !important;
          }
        `}</style>
        
        {/* ================================================================ */}
        {/* VIEW 1: TEMPLATE MASTER LIST VIEW                                */}
        {/* ================================================================ */}
        {viewMode === 'list' && (
          <div className="space-y-6">
            <div className="flex items-center justify-between flex-wrap gap-4">
              <div>
                <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
                  <Wand2 className="w-7 h-7 text-[#0052FF]" /> MS Word Style Document Designer
                </h1>
                <p className="text-sm text-slate-500 mt-1">
                  Visually design Appointment Orders, Offer Letters &amp; HR documents with paste HTML normalizer, paragraph spacing, and live A4 preview.
                </p>
              </div>

              <div className="flex items-center gap-3">
                <select
                  value={selectedCompany}
                  onChange={e => setSelectedCompany(e.target.value)}
                  className="border border-slate-300 rounded-xl px-3 py-2 text-sm bg-white font-medium"
                >
                  {companies.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                </select>

                {activeTab === 'templates' ? (
                  <Button onClick={handleOpenDesignerNew} className="bg-[#0052FF] hover:bg-[#0040CC] text-white rounded-xl shadow-md font-semibold">
                    <Plus className="w-4 h-4 mr-1.5" /> Open MS Word Designer
                  </Button>
                ) : (
                  <Button onClick={() => setShowPolicyModal(true)} className="bg-indigo-600 hover:bg-indigo-700 text-white rounded-xl">
                    <Plus className="w-4 h-4 mr-1.5" /> Add Policy Clause
                  </Button>
                )}
              </div>
            </div>

            {/* Tabs Header */}
            <div className="flex border-b border-slate-200 gap-6 text-sm font-semibold">
              <button
                onClick={() => setActiveTab('templates')}
                className={`py-2.5 flex items-center gap-2 border-b-2 transition-colors ${
                  activeTab === 'templates' ? 'border-[#0052FF] text-[#0052FF]' : 'border-transparent text-slate-500 hover:text-slate-700'
                }`}
              >
                <Layout className="w-4 h-4" /> Visual Document Templates
              </button>
              <button
                onClick={() => setActiveTab('policies')}
                className={`py-2.5 flex items-center gap-2 border-b-2 transition-colors ${
                  activeTab === 'policies' ? 'border-[#0052FF] text-[#0052FF]' : 'border-transparent text-slate-500 hover:text-slate-700'
                }`}
              >
                <ShieldCheck className="w-4 h-4" /> Company Policy Clauses
              </button>
            </div>

            {activeTab === 'templates' ? (
              <div className="space-y-6">
                {/* Document Types Catalog Pills */}
                <div className="flex flex-wrap gap-2">
                  {catalog.map(cat => (
                    <button
                      key={cat.type}
                      onClick={() => setActiveDocType(cat.type)}
                      className={`px-3.5 py-1.5 rounded-full text-xs font-semibold transition-all ${
                        activeDocType === cat.type
                          ? 'bg-[#0052FF] text-white shadow-sm'
                          : 'bg-white border border-slate-200 text-slate-600 hover:border-slate-300 hover:bg-slate-50'
                      }`}
                    >
                      {cat.label}
                    </button>
                  ))}
                </div>

                {/* Templates Cards Grid */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {loading ? (
                    <div className="col-span-2 py-16 text-center text-slate-400">
                      <RefreshCw className="w-6 h-6 animate-spin mx-auto mb-2" /> Loading templates...
                    </div>
                  ) : templates.length === 0 ? (
                    <div className="col-span-2 bg-white border border-slate-200 rounded-2xl p-12 text-center">
                      <FileCode className="w-12 h-12 text-slate-300 mx-auto mb-3" />
                      <h3 className="text-base font-semibold text-slate-700">No Templates Found</h3>
                      <p className="text-xs text-slate-400 mt-1 max-w-md mx-auto">
                        Click <b>"Open MS Word Designer"</b> to build a corporate template visually.
                      </p>
                      <Button onClick={handleOpenDesignerNew} className="mt-4 bg-[#0052FF] hover:bg-[#0040CC] text-white rounded-xl">
                        <Plus className="w-4 h-4 mr-1.5" /> Launch Word Designer
                      </Button>
                    </div>
                  ) : (
                    templates.map(tpl => {
                      const tplId = tpl.id || tpl._id;
                      return (
                        <div key={tplId} className="bg-white border border-slate-200 rounded-2xl p-5 shadow-sm space-y-3">
                          <div className="flex items-start justify-between">
                            <div>
                              <div className="flex items-center gap-2">
                                <h3 className="font-bold text-slate-900 text-base">{tpl.template_name}</h3>
                                <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-blue-100 text-blue-800">
                                  v{tpl.version || 1}
                                </span>
                                {tpl.is_default && (
                                  <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-800 flex items-center gap-1">
                                    <Star className="w-3 h-3 fill-emerald-600" /> Default
                                  </span>
                                )}
                              </div>
                              <p className="text-xs text-slate-500 mt-1">{tpl.description || 'No description'}</p>
                            </div>
                          </div>

                          <div
                            className="bg-slate-50 border border-slate-100 rounded-xl p-3 text-xs text-slate-600 line-clamp-3 font-sans editor-html-container"
                            dangerouslySetInnerHTML={{ __html: tpl.body_template }}
                          />

                          <div className="flex items-center justify-between text-xs pt-2 border-t border-slate-100">
                            <span className="text-slate-400">Effective: <b>{tpl.effective_from || 'Immediate'}</b></span>
                            <button
                              onClick={() => handleEditTemplate(tpl)}
                              className="px-3 py-1 bg-slate-900 hover:bg-slate-800 text-white rounded-lg font-medium flex items-center gap-1 transition-colors"
                            >
                              <Wrench className="w-3.5 h-3.5" /> Edit in Word Designer
                            </button>
                          </div>
                        </div>
                      );
                    })
                  )}
                </div>
              </div>
            ) : (
              /* Policy Clauses Master */
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {policies.map((pol, idx) => (
                  <div key={idx} className="bg-white border border-slate-200 rounded-2xl p-5 shadow-sm space-y-2">
                    <div className="flex items-center justify-between">
                      <h4 className="font-bold text-slate-900 text-sm">{pol.title}</h4>
                      <span className="font-mono text-[10px] bg-slate-100 text-slate-600 px-2 py-0.5 rounded">{pol.key}</span>
                    </div>
                    <p className="text-xs text-slate-600 bg-slate-50 p-3 rounded-xl border border-slate-100">
                      {pol.content}
                    </p>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* ================================================================ */}
        {/* VIEW 2: MS WORD WYSIWYG RICH TEXT 3-PANEL DESIGNER               */}
        {/* ================================================================ */}
        {viewMode === 'designer' && (
          <div className="bg-slate-900 text-white rounded-2xl shadow-2xl border border-slate-800 overflow-hidden flex flex-col h-[94vh]" data-testid="word-designer">
            
            {/* TOP BAR: DOCUMENT HEADER & ACTIONS */}
            <div className="bg-slate-950 px-6 py-2.5 border-b border-slate-800 flex items-center justify-between shrink-0">
              <div className="flex items-center gap-3">
                <FileCheck className="w-6 h-6 text-[#0052FF]" />
                <div>
                  <input
                    type="text"
                    value={designerState.template_name}
                    onChange={e => setDesignerState({ ...designerState, template_name: e.target.value })}
                    className="bg-transparent text-white font-bold text-base outline-none border-b border-dashed border-slate-700 focus:border-blue-500"
                  />
                  <div className="text-[11px] text-slate-400 font-mono flex items-center gap-2">
                    <span>Type: <span className="capitalize">{designerState.doc_type.replace(/_/g, ' ')}</span></span>
                    <span>·</span>
                    <span className="text-blue-400 font-bold">{dynamicPages.length} {dynamicPages.length === 1 ? 'Page' : 'Pages'} (Auto-Expanding)</span>
                  </div>
                </div>
              </div>

              {/* Action Buttons */}
              <div className="flex items-center gap-2">
                {/* Zoom Controls */}
                <div className="flex items-center bg-slate-900 border border-slate-800 rounded-xl px-2 py-1 text-xs text-slate-300">
                  <button onClick={() => setZoomLevel(z => Math.max(50, z - 10))} className="p-1 hover:text-white">
                    <ZoomOut className="w-3.5 h-3.5" />
                  </button>
                  <span className="w-10 text-center font-mono font-bold">{zoomLevel}%</span>
                  <button onClick={() => setZoomLevel(z => Math.min(200, z + 10))} className="p-1 hover:text-white">
                    <ZoomIn className="w-3.5 h-3.5" />
                  </button>
                </div>

                <Button onClick={() => setShowFieldPicker(true)} className="bg-blue-600 hover:bg-blue-500 text-white text-xs h-9 rounded-xl font-bold">
                  <Sparkles className="w-3.5 h-3.5 mr-1" /> Insert Merge Field
                </Button>

                <Button onClick={handleSaveDesignerTemplate} className="bg-indigo-600 hover:bg-indigo-500 text-white text-xs h-9 rounded-xl font-bold">
                  <Save className="w-3.5 h-3.5 mr-1" /> Save Template
                </Button>

                <button onClick={() => setViewMode('list')} className="text-slate-400 hover:text-white p-2">
                  <X className="w-5 h-5" />
                </button>
              </div>
            </div>

            {/* SECONDARY TOOLBAR: FULL MS WORD WYSIWYG FORMATTING RIBBON */}
            <div className="bg-slate-900 px-6 py-2 border-b border-slate-800 flex items-center justify-between gap-3 text-xs overflow-x-auto shrink-0">
              
              {/* Font Family & Size */}
              <div className="flex items-center gap-1.5">
                <select
                  onChange={e => execCmd('fontName', e.target.value)}
                  className="bg-slate-950 text-white border border-slate-800 rounded-lg px-2 py-1 text-xs font-medium outline-none"
                >
                  <option value="Inter">Inter (Sans)</option>
                  <option value="Roboto">Roboto</option>
                  <option value="Georgia">Georgia (Serif)</option>
                  <option value="Times New Roman">Times New Roman</option>
                  <option value="Courier New">Courier</option>
                </select>

                <select
                  onChange={e => execCmd('fontSize', e.target.value)}
                  className="bg-slate-950 text-white border border-slate-800 rounded-lg px-2 py-1 text-xs font-mono outline-none"
                >
                  <option value="3">10pt</option>
                  <option value="4">12pt</option>
                  <option value="5">14pt</option>
                  <option value="6">18pt</option>
                  <option value="7">24pt</option>
                </select>
              </div>

              {/* Text Styling */}
              <div className="flex items-center bg-slate-950 border border-slate-800 rounded-xl p-1 gap-0.5">
                <button onClick={() => execCmd('bold')} title="Bold (Ctrl+B)" className="p-1.5 hover:bg-slate-800 rounded text-slate-300 hover:text-white font-bold"><Bold className="w-3.5 h-3.5" /></button>
                <button onClick={() => execCmd('italic')} title="Italic (Ctrl+I)" className="p-1.5 hover:bg-slate-800 rounded text-slate-300 hover:text-white"><Italic className="w-3.5 h-3.5" /></button>
                <button onClick={() => execCmd('underline')} title="Underline (Ctrl+U)" className="p-1.5 hover:bg-slate-800 rounded text-slate-300 hover:text-white"><Underline className="w-3.5 h-3.5" /></button>
                <button onClick={() => execCmd('strikeThrough')} title="Strikethrough" className="p-1.5 hover:bg-slate-800 rounded text-slate-300 hover:text-white"><Strikethrough className="w-3.5 h-3.5" /></button>
              </div>

              {/* Alignment */}
              <div className="flex items-center bg-slate-950 border border-slate-800 rounded-xl p-1 gap-0.5">
                <button onClick={() => execCmd('justifyLeft')} title="Align Left" className="p-1.5 hover:bg-slate-800 rounded text-slate-300 hover:text-white"><AlignLeft className="w-3.5 h-3.5" /></button>
                <button onClick={() => execCmd('justifyCenter')} title="Center" className="p-1.5 hover:bg-slate-800 rounded text-slate-300 hover:text-white"><AlignCenter className="w-3.5 h-3.5" /></button>
                <button onClick={() => execCmd('justifyRight')} title="Align Right" className="p-1.5 hover:bg-slate-800 rounded text-slate-300 hover:text-white"><AlignRight className="w-3.5 h-3.5" /></button>
                <button onClick={() => execCmd('justifyFull')} title="Justify" className="p-1.5 hover:bg-slate-800 rounded text-slate-300 hover:text-white"><AlignJustify className="w-3.5 h-3.5" /></button>
              </div>

              {/* Lists & Headings */}
              <div className="flex items-center bg-slate-950 border border-slate-800 rounded-xl p-1 gap-0.5">
                <button onClick={() => execCmd('insertUnorderedList')} title="Bullet List" className="p-1.5 hover:bg-slate-800 rounded text-slate-300 hover:text-white"><List className="w-3.5 h-3.5" /></button>
                <button onClick={() => execCmd('insertOrderedList')} title="Numbered List" className="p-1.5 hover:bg-slate-800 rounded text-slate-300 hover:text-white"><ListOrdered className="w-3.5 h-3.5" /></button>
                <button onClick={() => execCmd('formatBlock', '<h2>')} title="Heading 2" className="p-1.5 hover:bg-slate-800 rounded text-slate-300 hover:text-white"><Heading2 className="w-3.5 h-3.5" /></button>
                <button onClick={() => execCmd('formatBlock', '<p>')} title="Normal Paragraph" className="p-1.5 hover:bg-slate-800 rounded text-slate-300 hover:text-white"><Type className="w-3.5 h-3.5" /></button>
              </div>

              {/* Insert Table & Line */}
              <div className="flex items-center bg-slate-950 border border-slate-800 rounded-xl p-1 gap-0.5">
                <button
                  onClick={() => execCmd('insertHTML', '<table border="1" style="width:100%;border-collapse:collapse;margin:10px 0;"><tr><td style="padding:8px;border:1px solid #ccc;">Header 1</td><td style="padding:8px;border:1px solid #ccc;">Header 2</td></tr><tr><td style="padding:8px;border:1px solid #ccc;">Data 1</td><td style="padding:8px;border:1px solid #ccc;">Data 2</td></tr></table>')}
                  title="Insert Table"
                  className="p-1.5 hover:bg-slate-800 rounded text-slate-300 hover:text-white"
                >
                  <Table className="w-3.5 h-3.5" />
                </button>
                <button onClick={() => execCmd('insertHorizontalRule')} title="Insert Horizontal Rule" className="p-1.5 hover:bg-slate-800 rounded text-slate-300 hover:text-white"><Minus className="w-3.5 h-3.5" /></button>
                <button onClick={() => execCmd('removeFormat')} title="Clear Formatting" className="p-1.5 hover:bg-slate-800 rounded text-slate-300 hover:text-white"><RemoveFormatting className="w-3.5 h-3.5" /></button>
              </div>
            </div>

            {/* 3-PANEL WORKSPACE CONTAINER */}
            <div className="flex-1 grid grid-cols-12 overflow-hidden">
              
              {/* LEFT PANEL (3 COLS): DOCUMENT STRUCTURE */}
              <div className="col-span-3 bg-slate-900 border-r border-slate-800 p-4 overflow-y-auto space-y-4 text-xs">
                <div className="flex items-center justify-between">
                  <h3 className="font-bold uppercase tracking-wider text-slate-400 text-[11px] flex items-center gap-1.5">
                    <Layers className="w-3.5 h-3.5 text-blue-400" /> Document Structure
                  </h3>
                </div>

                <div className="space-y-2">
                  {designerState.sections.map((sec, idx) => {
                    const isSelected = designerState.activeSectionId === sec.id;
                    return (
                      <div
                        key={sec.id}
                        onClick={() => setDesignerState({ ...designerState, activeSectionId: sec.id })}
                        className={`p-3 rounded-xl border transition-all cursor-pointer flex items-center justify-between ${
                          isSelected
                            ? 'bg-blue-950/80 border-blue-500 text-white font-bold shadow-md'
                            : sec.enabled
                            ? 'bg-slate-850 border-slate-800 text-slate-300 hover:bg-slate-800'
                            : 'bg-slate-950/50 border-slate-850 text-slate-600 opacity-60'
                        }`}
                      >
                        <div className="flex items-center gap-2 min-w-0">
                          <button
                            type="button"
                            onClick={(e) => { e.stopPropagation(); toggleSectionEnabled(sec.id); }}
                            className="text-slate-400 hover:text-white"
                          >
                            {sec.enabled ? <Eye className="w-3.5 h-3.5 text-emerald-400" /> : <EyeOff className="w-3.5 h-3.5 text-slate-600" />}
                          </button>
                          <span className="truncate">{sec.title}</span>
                        </div>

                        <div className="flex items-center gap-1 shrink-0" onClick={e => e.stopPropagation()}>
                          <button onClick={() => moveSection(idx, -1)} disabled={idx === 0} className="p-1 text-slate-500 hover:text-white disabled:opacity-30">
                            <MoveUp className="w-3 h-3" />
                          </button>
                          <button onClick={() => moveSection(idx, 1)} disabled={idx === designerState.sections.length - 1} className="p-1 text-slate-500 hover:text-white disabled:opacity-30">
                            <MoveDown className="w-3 h-3" />
                          </button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* CENTER PANEL (6 COLS): AUTO-EXPANDING WYSIWYG LIVE A4 CANVAS */}
              <div className="col-span-6 bg-slate-950 p-6 overflow-y-auto flex flex-col items-center gap-8">
                
                {/* Dynamically Render Stacked A4 Pages */}
                {dynamicPages.map((pageItems, pageIdx) => (
                  <div
                    key={pageIdx}
                    className="w-full max-w-[210mm] min-h-[297mm] bg-white text-slate-900 shadow-2xl rounded-sm p-10 space-y-5 text-xs font-sans relative transition-all flex flex-col justify-between word-preview-canvas"
                    style={{
                      transform: `scale(${zoomLevel / 100})`,
                      transformOrigin: 'top center',
                      fontFamily: designerState.theme.fontFamily,
                      fontSize: designerState.theme.fontSize,
                    }}
                  >
                    {/* Watermark Overlay */}
                    {designerState.watermark.enabled && (
                      <div className="absolute inset-0 flex items-center justify-center pointer-events-none select-none overflow-hidden">
                        <span
                          className="font-black uppercase tracking-widest pointer-events-none opacity-20"
                          style={{
                            transform: `rotate(-${designerState.watermark.rotation}deg)`,
                            fontSize: '4.5rem',
                            color: designerState.watermark.color,
                          }}
                        >
                          {designerState.watermark.text || 'CONFIDENTIAL'}
                        </span>
                      </div>
                    )}

                    {/* Top Content Area */}
                    <div className="space-y-4">
                      {pageItems.map((item, itemIdx) => {
                        if (item.type === 'header') {
                          return (
                            <div
                              key={itemIdx}
                              className="pb-3 flex items-center justify-between border-b-2"
                              style={{ borderColor: designerState.theme.primaryColor }}
                            >
                              <div>
                                <h2 className="text-lg font-bold" style={{ color: designerState.theme.primaryColor }}>
                                  {SAMPLE_DATA.COMPANY_NAME}
                                </h2>
                                <p className="text-[10px] text-slate-500">{SAMPLE_DATA.COMPANY_ADDRESS}</p>
                              </div>
                              <div
                                className="w-10 h-10 text-white rounded-lg flex items-center justify-center font-bold text-sm"
                                style={{ background: designerState.theme.primaryColor }}
                              >
                                HECS
                              </div>
                            </div>
                          );
                        }

                        if (item.type === 'ref_date') {
                          return (
                            <div key={itemIdx} className="flex justify-between text-[11px] font-semibold text-slate-700">
                              <span>Ref: {SAMPLE_DATA.REF_NUMBER}</span>
                              <span>Date: {SAMPLE_DATA.TODAY_DATE}</span>
                            </div>
                          );
                        }

                        if (item.type === 'recipient') {
                          return (
                            <div key={itemIdx} className="space-y-0.5 text-xs">
                              <div><b>To,</b></div>
                              <div className="font-bold text-slate-900">{SAMPLE_DATA.EMPLOYEE_NAME}</div>
                              <div className="text-slate-600">{SAMPLE_DATA.ADDRESS}</div>
                              <div className="text-slate-500 text-[11px]">Mobile: {SAMPLE_DATA.MOBILE} | Email: {SAMPLE_DATA.EMAIL}</div>
                            </div>
                          );
                        }

                        if (item.type === 'subject') {
                          return (
                            <div
                              key={itemIdx}
                              className="text-center font-bold uppercase border-y py-1.5 text-xs bg-slate-50"
                              style={{ color: designerState.theme.primaryColor, borderColor: '#E2E8F0' }}
                            >
                              {item.text}
                            </div>
                          );
                        }

                        if (item.type === 'greeting') {
                          return (
                            <div key={itemIdx} className="font-semibold text-slate-800">
                              {item.text}
                            </div>
                          );
                        }

                        if (item.type === 'body_paragraph') {
                          return (
                            <div key={itemIdx}>
                              {renderFormattedHtmlWithChips(item.html)}
                            </div>
                          );
                        }

                        if (item.type === 'terms_title') {
                          return (
                            <div key={itemIdx} className="font-bold text-xs uppercase pt-2 border-t border-slate-200" style={{ color: designerState.theme.primaryColor }}>
                              Terms &amp; Conditions of Employment
                            </div>
                          );
                        }

                        if (item.type === 'terms_clause') {
                          return (
                            <div key={itemIdx} className="text-[11px] text-slate-700">
                              <b>{item.clause.title}:</b> {item.clause.content.replace(/\{\{\s*PROBATION_PERIOD\s*\}\}/gi, SAMPLE_DATA.PROBATION_PERIOD).replace(/\{\{\s*WORK_LOCATION\s*\}\}/gi, SAMPLE_DATA.WORK_LOCATION)}
                            </div>
                          );
                        }

                        if (item.type === 'salary_table') {
                          const annexCfg = designerState.annexureConfig || {};
                          const sections = annexCfg.sections || [];
                          const isTraditional = annexCfg.layout_type === 'traditional_breakup' || sections.length > 0;

                          return (
                            <div key={itemIdx} className="pt-3 border-t-2 border-dashed border-slate-300 space-y-2">
                              <div className="text-center font-bold text-xs uppercase text-slate-900">
                                Annexure I — Dynamic Salary &amp; CTC Breakup
                              </div>
                              <table className="w-full text-[10px] border border-slate-300">
                                <thead className="text-white font-bold" style={{ background: designerState.theme.secondaryColor }}>
                                  <tr>
                                    <th className="p-1.5 text-left">Salary Component Head</th>
                                    <th className="p-1.5 text-center">Category</th>
                                    <th className="p-1.5 text-right">Monthly (₹)</th>
                                    <th className="p-1.5 text-right">Annual (₹)</th>
                                  </tr>
                                </thead>
                                <tbody className="divide-y divide-slate-200">
                                  {isTraditional ? (
                                    sections.map((sec) => {
                                      const secComps = sec.components || [];
                                      const catType = sec.category_type || 'earning';
                                      const catLabel = catType.charAt(0).toUpperCase() + catType.slice(1);

                                      return (
                                        <React.Fragment key={sec.id}>
                                          <tr className="bg-slate-100 font-bold text-slate-800 border-t border-b border-slate-300">
                                            <td colSpan={4} className="p-1.5 text-xs text-blue-900 bg-blue-50/60 font-extrabold">{sec.heading}</td>
                                          </tr>
                                          {secComps.filter(c => c.visible !== false).map((c) => {
                                            const matchMaster = ALL_SALARY_COMPONENTS.find(ac => ac.key === c.head_code);
                                            const mVal = matchMaster ? matchMaster.defaultMonthly : 0;
                                            return (
                                              <tr key={c.head_code} className="hover:bg-slate-50">
                                                <td className="p-1.5 font-medium pl-4">{c.display_label || matchMaster?.label || c.head_code}</td>
                                                <td className="p-1.5 text-center text-slate-500 capitalize">{catLabel}</td>
                                                <td className="p-1.5 text-right font-mono">{mVal.toLocaleString('en-IN', { minimumFractionDigits: 2 })}</td>
                                                <td className="p-1.5 text-right font-mono">{(mVal * 12).toLocaleString('en-IN', { minimumFractionDigits: 2 })}</td>
                                              </tr>
                                            );
                                          })}
                                          {sec.show_subtotal && (
                                            <tr className="bg-slate-50 font-bold border-t border-slate-200">
                                              <td className="p-1.5 pl-4">{sec.subtotal_label || `Subtotal (${sec.heading})`}</td>
                                              <td className="p-1.5 text-center text-slate-600">{catLabel}</td>
                                              <td className="p-1.5 text-right font-mono">
                                                ₹ {secComps.filter(c => c.visible !== false).reduce((acc, curr) => {
                                                  const mm = ALL_SALARY_COMPONENTS.find(ac => ac.key === curr.head_code);
                                                  return acc + (mm ? mm.defaultMonthly : 0);
                                                }, 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}
                                              </td>
                                              <td className="p-1.5 text-right font-mono">
                                                ₹ {(secComps.filter(c => c.visible !== false).reduce((acc, curr) => {
                                                  const mm = ALL_SALARY_COMPONENTS.find(ac => ac.key === curr.head_code);
                                                  return acc + (mm ? mm.defaultMonthly : 0);
                                                }, 0) * 12).toLocaleString('en-IN', { minimumFractionDigits: 2 })}
                                              </td>
                                            </tr>
                                          )}
                                        </React.Fragment>
                                      );
                                    })
                                  ) : (
                                    designerState.salaryComponents.filter(c => c.enabled).map((comp) => (
                                      <tr key={comp.key}>
                                        <td className="p-1.5 font-medium">{comp.label}</td>
                                        <td className="p-1.5 text-center capitalize text-slate-500">{comp.type}</td>
                                        <td className="p-1.5 text-right font-mono">{(comp.monthly || 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}</td>
                                        <td className="p-1.5 text-right font-mono">{((comp.monthly || 0) * 12).toLocaleString('en-IN', { minimumFractionDigits: 2 })}</td>
                                      </tr>
                                    ))
                                  )}
                                  <tr className="bg-emerald-50 text-emerald-900 font-bold border-t border-emerald-200">
                                    <td className="p-1.5">NET TAKE-HOME SALARY (Gross - Deductions)</td>
                                    <td className="p-1.5 text-center">Take Home</td>
                                    <td className="p-1.5 text-right font-mono">₹ {calculatedSalaryTotals.takeHomeMonthly.toLocaleString('en-IN', { minimumFractionDigits: 2 })}</td>
                                    <td className="p-1.5 text-right font-mono">₹ {calculatedSalaryTotals.takeHomeAnnual.toLocaleString('en-IN', { minimumFractionDigits: 2 })}</td>
                                  </tr>
                                  <tr className="font-bold text-white shadow-xs" style={{ background: designerState.theme.primaryColor }}>
                                    <td className="p-1.5">TOTAL COST TO COMPANY (FIXED CTC)</td>
                                    <td className="p-1.5 text-center">CTC</td>
                                    <td className="p-1.5 text-right font-mono">₹ {calculatedSalaryTotals.ctcMonthly.toLocaleString('en-IN', { minimumFractionDigits: 2 })}</td>
                                    <td className="p-1.5 text-right font-mono">₹ {calculatedSalaryTotals.ctcAnnual.toLocaleString('en-IN', { minimumFractionDigits: 2 })}</td>
                                  </tr>
                                </tbody>
                              </table>
                            </div>
                          );
                        }

                        if (item.type === 'signature') {
                          return (
                            <div key={itemIdx} className="pt-6 border-t border-slate-200 flex justify-between items-end text-xs">
                              <div>
                                <div className="font-bold">For {SAMPLE_DATA.COMPANY_NAME}</div>
                                <div className="h-8"></div>
                                <div className="border-t border-slate-400 pt-1 font-bold">Authorized Signatory / HR</div>
                                <div className="text-[10px] text-slate-500">Date: {SAMPLE_DATA.TODAY_DATE}</div>
                              </div>

                              <div className="text-right">
                                <div className="font-bold">Employee Acceptance</div>
                                <div className="h-8"></div>
                                <div className="border-t border-slate-400 pt-1 font-bold">{SAMPLE_DATA.EMPLOYEE_NAME}</div>
                                <div className="text-[10px] text-slate-500">Date: _______________</div>
                              </div>
                            </div>
                          );
                        }

                        return null;
                      })}
                    </div>

                    {/* Dynamic Footer with Page Numbers */}
                    <div className="pt-4 border-t border-slate-200 flex justify-between items-center text-[10px] text-slate-400 font-mono">
                      <span>Confidential — For Addressee Only</span>
                      <span>Page {pageIdx + 1} of {dynamicPages.length}</span>
                    </div>
                  </div>
                ))}
              </div>

              {/* RIGHT PANEL (3 COLS): INSPECTOR & RICH TEXT CANVAS */}
              <div className="col-span-3 bg-slate-900 border-l border-slate-800 p-4 overflow-y-auto space-y-4 text-xs">
                
                {/* Inspector Tabs */}
                <div className="flex border-b border-slate-800 gap-2 pb-2">
                  <button
                    onClick={() => setInspectorTab('properties')}
                    className={`px-2.5 py-1 rounded-lg font-bold transition-colors ${inspectorTab === 'properties' ? 'bg-blue-600 text-white' : 'text-slate-400 hover:text-white'}`}
                  >
                    Editor Canvas
                  </button>
                  <button
                    onClick={() => setInspectorTab('watermark')}
                    className={`px-2.5 py-1 rounded-lg font-bold transition-colors ${inspectorTab === 'watermark' ? 'bg-blue-600 text-white' : 'text-slate-400 hover:text-white'}`}
                  >
                    Watermark
                  </button>
                  <button
                    onClick={() => setInspectorTab('salary')}
                    className={`px-2.5 py-1 rounded-lg font-bold transition-colors ${inspectorTab === 'salary' ? 'bg-blue-600 text-white' : 'text-slate-400 hover:text-white'}`}
                  >
                    Salary
                  </button>
                </div>

                {/* TAB 1: WYSIWYG RICH CONTENTEDITABLE CANVAS WITH PASTE NORMALIZER */}
                {inspectorTab === 'properties' && (
                  <div className="space-y-4">
                    <h4 className="font-bold text-white text-xs flex items-center gap-1.5">
                      <Wrench className="w-3.5 h-3.5 text-blue-400" /> Rich Word Editor Canvas
                    </h4>

                    {activeSection.id === 'body' && (
                      <div className="space-y-3">
                        <label className="block font-bold text-slate-300 mb-1">
                          Body Content (Normalizing Paste &amp; Spacing)
                        </label>
                        <div
                          ref={editorRef}
                          contentEditable
                          suppressContentEditableWarning
                          onPaste={(e) => {
                            e.preventDefault();
                            const htmlData = e.clipboardData.getData('text/html');
                            const textData = e.clipboardData.getData('text/plain');
                            if (htmlData) {
                              const cleaned = cleanPastedHtml(htmlData);
                              execCmd('insertHTML', cleaned);
                            } else if (textData) {
                              const formatted = textData.split('\n\n').map(p => `<p>${p.replace(/\n/g, '<br/>')}</p>`).join('');
                              execCmd('insertHTML', formatted);
                            }
                          }}
                          onInput={() => {
                            if (editorRef.current) {
                              const html = cleanPastedHtml(editorRef.current.innerHTML);
                              setDesignerState(prev => ({
                                ...prev,
                                sections: prev.sections.map(s => s.id === 'body' ? { ...s, text: html } : s)
                              }));
                            }
                          }}
                          className="w-full min-h-[220px] p-3 bg-white text-slate-900 border border-slate-300 rounded-xl font-sans text-xs outline-none focus:ring-2 focus:ring-blue-500 overflow-y-auto leading-relaxed shadow-inner word-editor-canvas"
                          style={{ fontFamily: designerState.theme.fontFamily }}
                        />
                        <p className="text-[10px] text-slate-400">
                          Pasted content from Word, Docs, or Outlook is automatically sanitized. Extra gaps and Office XML styles are removed.
                        </p>
                        <Button onClick={() => setShowFieldPicker(true)} className="w-full bg-blue-600 hover:bg-blue-500 text-white font-bold text-xs">
                          + Insert Dynamic Field Chip
                        </Button>
                      </div>
                    )}

                    {activeSection.id === 'terms' && (
                      <div className="space-y-3">
                        <label className="block font-bold text-slate-300">Select Clauses from Library</label>
                        <div className="space-y-2 bg-slate-950 p-3 rounded-xl border border-slate-800 max-h-72 overflow-y-auto">
                          {CLAUSE_LIBRARY.map(c => {
                            const isChecked = (activeSection.clauses || []).includes(c.key);
                            return (
                              <label key={c.key} className="flex items-start gap-2 cursor-pointer text-slate-300">
                                <input
                                  type="checkbox"
                                  checked={isChecked}
                                  onChange={() => {
                                    const clauses = isChecked
                                      ? (activeSection.clauses || []).filter(k => k !== c.key)
                                      : [...(activeSection.clauses || []), c.key];
                                    setDesignerState(prev => ({
                                      ...prev,
                                      sections: prev.sections.map(s => s.id === 'terms' ? { ...s, clauses } : s)
                                    }));
                                  }}
                                  className="w-3.5 h-3.5 rounded text-blue-600 mt-0.5"
                                />
                                <div>
                                  <div className="font-bold text-[11px] text-white">{c.title}</div>
                                  <div className="text-[10px] text-slate-400 line-clamp-1">{c.content}</div>
                                </div>
                              </label>
                            );
                          })}
                        </div>
                      </div>
                    )}

                    {activeSection.id === 'subject' && (
                      <div className="space-y-3">
                        <div>
                          <label className="block font-bold text-slate-300 mb-1">Subject Line Text</label>
                          <input
                            type="text"
                            value={activeSection.text || ''}
                            onChange={e => {
                              const val = e.target.value;
                              setDesignerState(prev => ({
                                ...prev,
                                sections: prev.sections.map(s => s.id === 'subject' ? { ...s, text: val } : s)
                              }));
                            }}
                            className="w-full h-9 px-2.5 bg-slate-950 border border-slate-800 rounded-xl text-white font-medium text-xs outline-none"
                          />
                        </div>
                      </div>
                    )}
                  </div>
                )}

                {/* TAB 2: WATERMARK BUILDER */}
                {inspectorTab === 'watermark' && (
                  <div className="space-y-3">
                    <h4 className="font-bold text-white text-xs flex items-center gap-1.5">
                      <Droplets className="w-3.5 h-3.5 text-[#0052FF]" /> Watermark Configuration
                    </h4>

                    <label className="flex items-center gap-2 cursor-pointer font-semibold text-slate-300">
                      <input
                        type="checkbox"
                        checked={designerState.watermark.enabled}
                        onChange={e => setDesignerState({
                          ...designerState,
                          watermark: { ...designerState.watermark, enabled: e.target.checked }
                        })}
                        className="w-4 h-4 rounded text-blue-600"
                      />
                      Enable Document Watermark
                    </label>

                    {designerState.watermark.enabled && (
                      <div className="space-y-3 bg-slate-950 p-3 rounded-xl border border-slate-800">
                        <div>
                          <label className="block font-semibold text-slate-400 mb-1">Watermark Text</label>
                          <input
                            type="text"
                            value={designerState.watermark.text}
                            onChange={e => setDesignerState({
                              ...designerState,
                              watermark: { ...designerState.watermark, text: e.target.value }
                            })}
                            className="w-full h-8 px-2 bg-slate-900 border border-slate-800 rounded-lg text-white font-mono uppercase"
                          />
                        </div>

                        <div>
                          <label className="block font-semibold text-slate-400 mb-1">Rotation Angle ({designerState.watermark.rotation}°)</label>
                          <input
                            type="range"
                            min="0"
                            max="90"
                            value={designerState.watermark.rotation}
                            onChange={e => setDesignerState({
                              ...designerState,
                              watermark: { ...designerState.watermark, rotation: Number(e.target.value) }
                            })}
                            className="w-full accent-blue-600"
                          />
                        </div>
                      </div>
                    )}
                  </div>
                )}

                {/* TAB 3: TRADITIONAL SALARY BREAKUP BUILDER */}
                {inspectorTab === 'salary' && (
                  <div className="space-y-4">
                    <div>
                      <h4 className="font-bold text-white text-xs flex items-center gap-1.5">
                        <DollarSign className="w-4 h-4 text-emerald-400" /> Salary Annexure Builder
                      </h4>
                      <p className="text-[11px] text-slate-400 mt-0.5">
                        Configure Annexure Layout Presets, Traditional Salary Breakup Sections, display labels, dynamic ordering, and visibility.
                      </p>
                    </div>

                    {/* 1. Layout Preset Picker */}
                    <div className="space-y-2">
                      <label className="block text-[11px] font-bold uppercase tracking-wider text-slate-300">
                        1. Select Annexure Layout Preset
                      </label>
                      <div className="grid grid-cols-1 gap-1.5">
                        {ANNEXURE_PRESETS.map((preset) => {
                          const isSelected = (designerState.annexureConfig?.layout_type || 'traditional_breakup') === preset.id;
                          return (
                            <button
                              key={preset.id}
                              onClick={() => {
                                setDesignerState(prev => ({
                                  ...prev,
                                  annexureConfig: {
                                    ...(prev.annexureConfig || {}),
                                    layout_type: preset.id,
                                    sections: preset.sections
                                  }
                                }));
                                toast.success(`Applied layout preset: ${preset.name}`);
                              }}
                              className={`p-2.5 rounded-xl border text-left transition-all ${
                                isSelected
                                  ? 'bg-emerald-950/80 border-emerald-500 text-white shadow-md'
                                  : 'bg-slate-950 border-slate-800 text-slate-300 hover:bg-slate-850'
                              }`}
                            >
                              <div className="font-bold text-xs text-white flex items-center justify-between">
                                {preset.name}
                                {isSelected && <Check className="w-3.5 h-3.5 text-emerald-400" />}
                              </div>
                              <div className="text-[10px] text-slate-400 mt-0.5">{preset.description}</div>
                            </button>
                          );
                        })}
                      </div>
                    </div>

                    {/* 2. Section Builder */}
                    <div className="space-y-3 pt-2 border-t border-slate-800">
                      <div className="flex items-center justify-between">
                        <label className="text-[11px] font-bold uppercase tracking-wider text-slate-300">
                          2. Configure Breakup Sections
                        </label>
                        <button
                          onClick={() => {
                            const newSecId = `sec_${Date.now()}`;
                            const newSec = {
                              id: newSecId,
                              heading: `New Custom Section`,
                              category_type: 'earning',
                              show_subtotal: true,
                              subtotal_label: `SUBTOTAL NEW SECTION`,
                              components: []
                            };
                            setDesignerState(prev => ({
                              ...prev,
                              annexureConfig: {
                                ...(prev.annexureConfig || {}),
                                sections: [...(prev.annexureConfig?.sections || []), newSec]
                              }
                            }));
                          }}
                          className="text-[11px] font-bold text-emerald-400 hover:text-emerald-300 flex items-center gap-1"
                        >
                          <Plus className="w-3 h-3" /> Add Section
                        </button>
                      </div>

                      <div className="space-y-2.5 max-h-96 overflow-y-auto pr-1">
                        {(designerState.annexureConfig?.sections || []).map((sec, secIdx) => (
                          <div key={sec.id} className="bg-slate-950 p-3 rounded-xl border border-slate-800 space-y-2.5">
                            <div className="flex items-center justify-between gap-2">
                              <input
                                type="text"
                                value={sec.heading}
                                onChange={(e) => {
                                  const newHeading = e.target.value;
                                  setDesignerState(prev => ({
                                    ...prev,
                                    annexureConfig: {
                                      ...(prev.annexureConfig || {}),
                                      sections: (prev.annexureConfig?.sections || []).map((s, idx) =>
                                        idx === secIdx ? { ...s, heading: newHeading } : s
                                      )
                                    }
                                  }));
                                }}
                                className="bg-slate-900 border border-slate-700 rounded-lg px-2.5 py-1 text-xs font-bold text-white outline-none w-full"
                              />
                              <select
                                value={sec.category_type || 'earning'}
                                onChange={(e) => {
                                  const newCat = e.target.value;
                                  setDesignerState(prev => ({
                                    ...prev,
                                    annexureConfig: {
                                      ...(prev.annexureConfig || {}),
                                      sections: (prev.annexureConfig?.sections || []).map((s, idx) =>
                                        idx === secIdx ? { ...s, category_type: newCat } : s
                                      )
                                    }
                                  }));
                                }}
                                className="bg-slate-900 border border-slate-700 rounded-lg px-2 py-1 text-[11px] font-medium text-slate-300 outline-none"
                              >
                                <option value="earning">Earning</option>
                                <option value="deduction">Deduction</option>
                                <option value="employer">Employer Perk</option>
                              </select>
                            </div>

                            {/* Section Components Mapping */}
                            <div className="space-y-1.5 pt-1">
                              <label className="text-[10px] font-bold text-slate-400 uppercase">Mapped Components</label>
                              {(sec.components || []).map((c, cIdx) => {
                                const masterComp = ALL_SALARY_COMPONENTS.find(ac => ac.key === c.head_code);
                                return (
                                  <div key={c.head_code} className="flex items-center gap-2 bg-slate-900 p-2 rounded-lg border border-slate-800 text-xs">
                                    <input
                                      type="checkbox"
                                      checked={c.visible !== false}
                                      onChange={(e) => {
                                        const isVis = e.target.checked;
                                        setDesignerState(prev => ({
                                          ...prev,
                                          annexureConfig: {
                                            ...(prev.annexureConfig || {}),
                                            sections: (prev.annexureConfig?.sections || []).map((s, sI) => {
                                              if (sI !== secIdx) return s;
                                              return {
                                                ...s,
                                                components: s.components.map((comp, cI) =>
                                                  cI === cIdx ? { ...comp, visible: isVis } : comp
                                                )
                                              };
                                            })
                                          }
                                        }));
                                      }}
                                      className="w-3.5 h-3.5 rounded text-emerald-600"
                                    />
                                    <span className="font-mono text-[10px] bg-slate-800 text-slate-300 px-1.5 py-0.5 rounded font-bold">{c.head_code}</span>
                                    <input
                                      type="text"
                                      value={c.display_label || masterComp?.label || c.head_code}
                                      onChange={(e) => {
                                        const newLbl = e.target.value;
                                        setDesignerState(prev => ({
                                          ...prev,
                                          annexureConfig: {
                                            ...(prev.annexureConfig || {}),
                                            sections: (prev.annexureConfig?.sections || []).map((s, sI) => {
                                              if (sI !== secIdx) return s;
                                              return {
                                                ...s,
                                                components: s.components.map((comp, cI) =>
                                                  cI === cIdx ? { ...comp, display_label: newLbl } : comp
                                                )
                                              };
                                            })
                                          }
                                        }));
                                      }}
                                      className="bg-slate-950 border border-slate-700 rounded px-2 py-0.5 text-xs text-white outline-none w-full font-medium"
                                      placeholder="Display Label"
                                    />
                                  </div>
                                );
                              })}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>

                    {/* 3. Add Component to Section Selector */}
                    <div className="space-y-2 pt-2 border-t border-slate-800">
                      <label className="block text-[11px] font-bold uppercase tracking-wider text-slate-300">
                        3. Add Salary Components to Sections
                      </label>
                      <div className="space-y-1.5 max-h-60 overflow-y-auto pr-1">
                        {ALL_SALARY_COMPONENTS.map((ac) => {
                          // Check which section this component belongs to
                          let activeSecId = '';
                          (designerState.annexureConfig?.sections || []).forEach(sec => {
                            if ((sec.components || []).some(c => c.head_code === ac.key)) {
                              activeSecId = sec.id;
                            }
                          });

                          return (
                            <div key={ac.key} className="flex items-center justify-between p-2 rounded-lg bg-slate-950 border border-slate-800 text-xs">
                              <div className="flex items-center gap-2">
                                <span className="font-bold text-white">{ac.label}</span>
                                <span className="font-mono text-[10px] text-slate-400">({ac.key})</span>
                              </div>
                              <select
                                value={activeSecId}
                                onChange={(e) => {
                                  const targetSecId = e.target.value;
                                  setDesignerState(prev => {
                                    const sections = (prev.annexureConfig?.sections || []).map(sec => {
                                      // Remove component from old section
                                      const filteredComps = (sec.components || []).filter(c => c.head_code !== ac.key);
                                      if (sec.id === targetSecId) {
                                        filteredComps.push({
                                          head_code: ac.key,
                                          display_label: ac.label,
                                          visible: true,
                                          display_order: filteredComps.length + 1
                                        });
                                      }
                                      return { ...sec, components: filteredComps };
                                    });
                                    return {
                                      ...prev,
                                      annexureConfig: { ...(prev.annexureConfig || {}), sections }
                                    };
                                  });
                                  toast.success(`Updated section mapping for ${ac.label}`);
                                }}
                                className="bg-slate-900 border border-slate-700 rounded px-2 py-1 text-[11px] font-medium text-slate-300 outline-none"
                              >
                                <option value="">-- Unmapped --</option>
                                {(designerState.annexureConfig?.sections || []).map(s => (
                                  <option key={s.id} value={s.id}>{s.heading}</option>
                                ))}
                              </select>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* DYNAMIC FIELD INSERTION POPUP */}
        {showFieldPicker && (
          <div className="fixed inset-0 z-50 bg-slate-900/60 backdrop-blur-sm flex items-center justify-center p-4">
            <div className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full overflow-hidden text-xs">
              <div className="flex items-center justify-between px-6 py-4 border-b border-slate-100 bg-slate-50">
                <h3 className="font-bold text-slate-900 text-base flex items-center gap-2">
                  <Sparkles className="w-5 h-5 text-[#0052FF]" /> Insert Dynamic Merge Field
                </h3>
                <button onClick={() => setShowFieldPicker(false)} className="text-slate-400 hover:text-slate-600 p-1">
                  <X className="w-5 h-5" />
                </button>
              </div>

              <div className="p-6 space-y-4 max-h-[70vh] overflow-y-auto">
                {DYNAMIC_FIELD_CATEGORIES.map(grp => {
                  const IconComp = grp.icon;
                  return (
                    <div key={grp.category} className="space-y-2">
                      <div className="font-bold text-slate-800 flex items-center gap-1.5">
                        <IconComp className="w-4 h-4 text-blue-600" /> {grp.category}
                      </div>
                      <div className="flex flex-wrap gap-2">
                        {grp.fields.map(f => (
                          <button
                            key={f.code}
                            onClick={() => handleInsertFieldChip(f)}
                            className="px-3 py-1.5 bg-blue-50 hover:bg-blue-100 text-blue-800 border border-blue-200 rounded-xl font-bold flex items-center gap-1 transition-all hover:scale-105"
                          >
                            + [{f.label}]
                          </button>
                        ))}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        )}

        {/* POLICY CLAUSE MODAL */}
        {showPolicyModal && (
          <div className="fixed inset-0 z-50 bg-slate-900/40 backdrop-blur-sm flex items-center justify-center p-4">
            <div className="bg-white rounded-2xl shadow-2xl max-w-lg w-full overflow-hidden">
              <div className="flex items-center justify-between px-6 py-4 border-b border-slate-100 bg-slate-50">
                <h3 className="font-bold text-slate-900 text-lg flex items-center gap-2">
                  <ShieldCheck className="w-5 h-5 text-indigo-600" /> Edit Policy Clause
                </h3>
                <button onClick={() => setShowPolicyModal(false)} className="text-slate-400 hover:text-slate-600 p-1">
                  <X className="w-5 h-5" />
                </button>
              </div>

              <form onSubmit={async (e) => {
                e.preventDefault();
                try {
                  await axios.post(`${API_URL}/documents/policies`, {
                    company_id: selectedCompany,
                    policy_key: policyForm.policy_key,
                    title: policyForm.title,
                    content: policyForm.content,
                    is_active: true,
                  }, { withCredentials: true });
                  toast.success('Policy clause updated');
                  setShowPolicyModal(false);
                  fetchPolicies();
                } catch (err) {
                  toast.error('Failed to save policy');
                }
              }} className="p-6 space-y-4 text-xs">
                <div>
                  <label className="block font-bold text-slate-700 uppercase mb-1">Clause Key</label>
                  <select
                    value={policyForm.policy_key}
                    onChange={e => setPolicyForm({ ...policyForm, policy_key: e.target.value })}
                    className="w-full h-10 px-3 bg-slate-50 border border-slate-200 rounded-xl outline-none"
                  >
                    <option value="probation">Probation &amp; Confirmation</option>
                    <option value="notice_period">Notice Period &amp; Resignation</option>
                    <option value="confidentiality">Confidentiality &amp; Non-Disclosure</option>
                    <option value="code_of_conduct">Code of Conduct &amp; Ethics</option>
                    <option value="transfer">Location &amp; Transferability</option>
                  </select>
                </div>

                <div>
                  <label className="block font-bold text-slate-700 uppercase mb-1">Clause Title *</label>
                  <input
                    type="text"
                    required
                    value={policyForm.title}
                    onChange={e => setPolicyForm({ ...policyForm, title: e.target.value })}
                    className="w-full h-10 px-3 bg-slate-50 border border-slate-200 rounded-xl outline-none"
                  />
                </div>

                <div>
                  <label className="block font-bold text-slate-700 uppercase mb-1">Clause Content *</label>
                  <textarea
                    rows={4}
                    required
                    value={policyForm.content}
                    onChange={e => setPolicyForm({ ...policyForm, content: e.target.value })}
                    className="w-full p-3 bg-slate-50 border border-slate-200 rounded-xl outline-none font-sans text-xs"
                  />
                </div>

                <div className="flex justify-end gap-2 pt-2">
                  <Button type="button" variant="outline" onClick={() => setShowPolicyModal(false)}>Cancel</Button>
                  <Button type="submit" className="bg-indigo-600 hover:bg-indigo-700 text-white">Save Clause</Button>
                </div>
              </form>
            </div>
          </div>
        )}

      </div>
    </MainLayout>
  );
}
